import { createContext, useContext, useEffect, useState } from 'react';
import type { AgentService } from 'services/agent/service';
import type { Presentation, Snapshot } from 'services/agent/model';
import { collectUnreadKinds, completedUnreadSessionId } from 'services/agent/session-unread';

export interface AgentContextValue {
  service: AgentService;
  mode: Presentation | null;
  setMode(mode: Presentation | null): void;
  fullPage: boolean;
  setPageContainer(element: HTMLDivElement | null): void;
  setSidebarContainer(element: HTMLElement | null): void;
  select(id?: string): void;
  expand(): void;
  returnToSource?: () => void;
  returnSourcePath?: string;
  navigate(payload: unknown, sessionId: string): void;
  cancelPageTask(): void;
  sidebarWidth: number;
  setSidebarWidth(width: number): void;
  pageContext(): unknown;
  storePlatformOptions?: ReadonlyArray<readonly [string, string]>;
  chooseStorePlatform(platform: string): void;
  businessPanelOpen: boolean;
  businessContainer: HTMLDivElement | null;
  setBusinessContainer(element: HTMLDivElement | null): void;
}
export const AgentContext = createContext<AgentContextValue | null>(null);
export const useAgent = () => useContext(AgentContext);
export function useAgentSnapshot(service: AgentService) {
  const [snapshot, setSnapshot] = useState(service.getSnapshot);
  useEffect(() => {
    const update = () => setSnapshot(service.getSnapshot());
    const unsubscribe = service.subscribe(update);
    update();
    return unsubscribe;
  }, [service]);
  return snapshot;
}

export function useAgentStatus() {
  const agent = useAgent();
  const [snapshot, setSnapshot] = useState<Snapshot | undefined>(() =>
    agent?.service.getSnapshot(),
  );
  useEffect(() => {
    if (!agent) {
      setSnapshot(undefined);
      return undefined;
    }
    const update = () => setSnapshot(agent.service.getSnapshot());
    const unsubscribe = agent.service.subscribe(update);
    update();
    return unsubscribe;
  }, [agent]);
  const current = snapshot ?? emptySnapshot;
  const unreadKinds = collectUnreadKinds(current.sessions, current.messages);
  return {
    snapshot: current,
    unreadKinds,
    completedUnreadSessionId: completedUnreadSessionId(
      unreadKinds,
      agent && (agent.fullPage || agent.mode) ? current.selectedId : undefined,
    ),
    isThinking: Object.keys(current.runs).some((id) => Boolean(current.runs[id])),
  };
}

const emptySnapshot: Snapshot = {
  sessions: [],
  sessionsById: {},
  page: 0,
  keyword: '',
  hasMore: false,
  uploading: 0,
  listLoading: false,
  listError: false,
  messages: {},
  drafts: {},
  pastedTexts: {},
  files: {},
  loading: {},
  errors: {},
  runs: {},
};
